/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {REPORT_OPTIONS} from '../common/report-translations';
import {ADN_REPORT_COMPONENTS, ADN_REPORT_TYPES} from '../report/report-types';

export interface ReportDateFormatOption {
  id: number;
  format: string;
  example: string;
}

export type ReportDateFormats = Record<string, ReportDateFormatOption[]>;

export function getReportTemplateTeamQueryParams(perms: any): Record<string, any> {
  const teamTypes: string[] = [];
  if (perms.AX_ADVERTISER_ADMIN === true || perms.AX_ADVERTISER_TEAM_ADMIN === true) {
    teamTypes.push('AX_ADVERTISER');
  }
  if (perms.AX_PUBLISHER_ADMIN === true || perms.AX_PUBLISHER_TEAM_ADMIN === true) {
    teamTypes.push('AX_PUBLISHER');
  }
  return {minimal: true, teamTypes: teamTypes.join(';')};
}

export function getAvailableReportLanguages(networkId: string): Record<string, string> {
  const reportOptions = _.find(REPORT_OPTIONS, {id: networkId}) || {langs: ['en']};
  const availableLangs: Record<string, string> = {};
  _.forEach(reportOptions.langs || ['en'], (lang) => {
    availableLangs[lang] = lang;
  });
  return availableLangs;
}

export function getReportTemplateTypes(isSuiPlatform: boolean): string[] {
  return _.map(_.filter(ADN_REPORT_TYPES, (rt) => {
    return rt.id !== 'SELF_SERVICE' || isSuiPlatform;
  }), (rt) => rt.id);
}

export function buildReportDateFormats(): ReportDateFormats {
  const dateFormats: ReportDateFormats = {};
  dateFormats.NONE = [
    {id: 0, format: '', example: 'Use locale defined in my user profile'},
    {id: 1, format: 'd/M/yyyy', example: '23/10/2018'},
    {id: 2, format: 'd-M-yyyy', example: '23-10-2018'},
    {id: 3, format: 'd.M.yyyy', example: '23.10.2018'},
    {id: 4, format: 'd MMM yyyy', example: '23 Oct 2018'},
    {id: 5, format: 'd-MMM-yyyy', example: '23-Oct-2018'},
    {id: 6, format: 'd.MMM.yyyy', example: '23.Oct.2018'},
    {id: 7, format: 'M/d/yyyy', example: '10/23/2018'},
    {id: 8, format: 'M-d-yyyy', example: '10-23-2018'},
    {id: 9, format: 'M.d.yyyy', example: '10.23.2018'},
    {id: 10, format: 'd MMM yyyy', example: '10 Oct 2018'},
    {id: 11, format: 'd-MMM-yyyy', example: '10-Oct-2018'},
    {id: 12, format: 'd.MMM.yyyy', example: '10.Oct.2018'},
    {id: 13, format: 'yyyy/M/d', example: '2018/10/23'},
    {id: 14, format: 'yyyy-M-d', example: '2018-10-23'},
    {id: 15, format: 'yyyy.M.d', example: '2018.10.23'},
    {id: 16, format: 'yyyy MMM d', example: '2018 Oct 10'},
    {id: 17, format: 'yyyy-MMM-d', example: '2018-Oct-10'}
  ];
  dateFormats.HOUR24 = [];
  dateFormats.AMPM = [];
  _.forEach(dateFormats.NONE, (entry) => {
    const hour24Entry = {id: entry.id, format: entry.format, example: entry.example};
    const amPmEntry = {id: entry.id, format: entry.format, example: entry.example};
    if (entry.format.length > 0) {
      hour24Entry.format += ' k:mm';
      hour24Entry.example += ' 18:49';
      amPmEntry.format += ' h:mm a';
      amPmEntry.example += ' 6:49 PM';
    }
    dateFormats.HOUR24.push(hour24Entry);
    dateFormats.AMPM.push(amPmEntry);
  });
  return dateFormats;
}

export function initReportTemplateComponent(component: any): void {
  component.parameters = component.parameters || {};
  component.parameters.dateFormat = component.parameters.dateFormat || '';
  if (!Object.prototype.hasOwnProperty.call(component.parameters, 'dateTimeFormatLineItem')) {
    component.parameters.timeFormatLineItem = 'HOUR24';
    component.parameters.dateTimeFormatLineItem = '';
  } else {
    component.parameters.dateTimeFormatLineItem = component.parameters.dateTimeFormatLineItem || '';
    if (component.parameters.dateTimeFormatLineItem.indexOf('k') > 0) {
      component.parameters.timeFormatLineItem = 'HOUR24';
    } else if (component.parameters.dateTimeFormatLineItem.indexOf('h') > 0) {
      component.parameters.timeFormatLineItem = 'AMPM';
    } else if (component.parameters.dateTimeFormatLineItem.length > 1) {
      component.parameters.timeFormatLineItem = 'NONE';
    } else {
      component.parameters.timeFormatLineItem = 'HOUR24';
    }
  }
  const componentId = _.get(component, ['reportComponent', 'id']);
  if (!componentId) {
    return;
  }
  const refComponent = ADN_REPORT_COMPONENTS[componentId];
  component.csv = _.get(refComponent, ['csv']) || false;
  component.translationOverrides = refComponent?.translationOverrides;
}

export function normalizeReportTemplateComponent(component: any): void {
  const componentId = _.get(component, ['reportComponent', 'id']);
  if (!componentId || !ADN_REPORT_COMPONENTS[componentId]) {
    return;
  }
  const refParams = ADN_REPORT_COMPONENTS[componentId].parameters;
  if (!component.parameters.heading) {
    component.parameters.heading = refParams.heading;
  }
  if (Object.prototype.hasOwnProperty.call(refParams, 'fields')) {
    component.parameters.fields = {
      ..._.cloneDeep(refParams.fields),
      ...(component.parameters.fields || {})
    };
  }
  if (Object.prototype.hasOwnProperty.call(refParams, 'sharedYAxis')
    && !Object.prototype.hasOwnProperty.call(component.parameters, 'sharedYAxis')) {
    component.parameters.sharedYAxis = refParams.sharedYAxis;
  }
  if (Object.prototype.hasOwnProperty.call(refParams, 'height')
    && !Object.prototype.hasOwnProperty.call(component.parameters, 'height')) {
    component.parameters.height = refParams.height;
  }
  initReportTemplateComponent(component);
}

export function createEmptyReportTypeComponents(): Record<string, { components: any[] }> {
  const reportTypeComponents: Record<string, { components: any[] }> = {};
  _.forEach(ADN_REPORT_TYPES, (rt) => {
    reportTypeComponents[rt.id] = {components: []};
  });
  return reportTypeComponents;
}

export function reportTemplateComponentLayoutKey(component: any): string {
  const componentId = component?.reportComponent?.id ?? '';
  return [
    componentId,
    component?.row ?? '',
    component?.col ?? '',
    component?.sizeX ?? '',
    component?.sizeY ?? ''
  ].join(':');
}

export function restoreReportTemplateComponentsAfterReload(
  reloadedComponents: any[],
  previousComponents: any[],
  nextUniqueId: () => number
): any[] {
  const previousByUniqueId = _.keyBy(
    _.filter(previousComponents, (component) => _.isFinite(component?.uniqueId)),
    'uniqueId'
  );
  const previousByLayout = _.keyBy(previousComponents, reportTemplateComponentLayoutKey);

  return _.map(reloadedComponents, (component) => {
    const restoredComponent = {...component};
    let previous = _.isFinite(restoredComponent.uniqueId)
      ? previousByUniqueId[restoredComponent.uniqueId]
      : undefined;
    if (!previous) {
      previous = previousByLayout[reportTemplateComponentLayoutKey(restoredComponent)];
    }
    if (previous) {
      if (_.isFinite(previous.uniqueId)) {
        restoredComponent.uniqueId = previous.uniqueId;
      }
      _.forEach(['col', 'row', 'sizeX', 'sizeY'], (field) => {
        if (restoredComponent[field] === undefined || restoredComponent[field] === null) {
          restoredComponent[field] = previous[field];
        }
      });
    }
    if (!_.isFinite(restoredComponent.uniqueId)) {
      restoredComponent.uniqueId = nextUniqueId();
    }
    return restoredComponent;
  });
}

export function initReportTemplateModel(
  model: any,
  savedModel: any,
  reportTypeComponents: Record<string, { components: any[] }>,
  nextUniqueId: () => number
): void {
  model.components = model.components || [];
  model.parameters = model.parameters || {};
  model.reportFormat = model.reportFormat || 'HTML';
  model.parameters.translation = model.parameters.translation || 'DEFAULT';

  if (!model.reportType) {
    return;
  }

  const savedComponents = savedModel?.reportType === model.reportType
    ? _.get(savedModel, 'components')
    : null;
  reportTypeComponents[model.reportType].components = _.cloneDeep(savedComponents || model.components || []);
  _.forEach(reportTypeComponents[model.reportType].components, (component) => {
    component.parameters = component.parameters || {};
    if (typeof component.reportComponent === 'string') {
      component.reportComponent = {id: component.reportComponent};
    }
    if (!_.isFinite(component.uniqueId)) {
      component.uniqueId = nextUniqueId();
    }
    normalizeReportTemplateComponent(component);
  });
}

export function applyDefaultReportTemplateTranslations(
  model: any,
  translations: Record<string, Record<string, string>>,
  langKey: string,
  fieldTranslate: (key: string, defaultValue?: string) => string
): void {
  _.forEach(translations.en, (val, key) => {
    model.parameters[key] = (translations[langKey] || translations.en)[key];
    if (!model.parameters[key]) {
      model.parameters[key] = fieldTranslate('report.field.' + _.camelCase(key.replace('translation', '')), val);
    }
  });
}

export function applyLoadedReportTranslationDefaults(
  model: any,
  translations: Record<string, Record<string, string>>,
  fieldTranslate: (key: string, defaultValue?: string) => string
): void {
  _.forEach(translations.en, (val, key) => {
    model.parameters[key] = model.parameters[key]
      || fieldTranslate('report.field.' + _.camelCase(key.replace('translation', '')), val);
  });
}

export function timeChangeReportComponent(component: any, dateFormats: ReportDateFormats): void {
  const allFormats = _.flattenDeep([
    dateFormats.NONE,
    dateFormats.HOUR24,
    dateFormats.AMPM
  ]);
  const prevFormat = _.find(allFormats, (df) => df.format === component.parameters.dateTimeFormatLineItem);
  const newFormat = _.find(dateFormats[component.parameters.timeFormatLineItem], (df) => df.id === prevFormat?.id);
  if (newFormat) {
    component.parameters.dateTimeFormatLineItem = newFormat.format;
  }
}

function sanitizeReportTemplateComponentForSave(component: any): void {
  delete component.uniqueId;
  delete component.csv;
  delete component.translationOverrides;
  if (component.parameters) {
    delete component.parameters.timeFormatLineItem;
  }
}

export function prepareReportTemplateForSubmit(
  model: any,
  reportTypeComponents: Record<string, { components: any[] }>,
  logoSpecify: boolean,
  fieldTranslate: (key: string) => string
): void {
  model.components = _.sortBy(
    _.cloneDeep(_.get(reportTypeComponents, [model.reportType, 'components']) || []),
    'row'
  );
  _.forEach(model.components, sanitizeReportTemplateComponentForSave);
  model.parameters = model.parameters || {};
  delete model.parameters.limit;

  if (!logoSpecify) {
    model.logo = '';
  }

  const doTranslation = (component: any, paramApi: string, paramUi: string) => {
    const paramToUse = _.get(component, ['translationOverrides', paramUi]) || paramUi;
    component.parameters[paramApi] = fieldTranslate('report.field.' + paramToUse);
  };

  if (model.reportTranslation) {
    const deleteTranslationsKeys: string[] = [];
    _.forEach(model.components, (component) => {
      _.forEach(component.parameters, (val, key) => {
        if (_.startsWith(key, 'translation')) {
          deleteTranslationsKeys.push(key);
        }
      });
    });
    _.forEach(model.parameters, (val, key) => {
      deleteTranslationsKeys.push(key);
    });
    _.forEach(deleteTranslationsKeys, (key) => {
      _.forEach(model.components, (component) => {
        delete component.parameters[key];
      });
      delete model.parameters[key];
    });
  } else {
    _.forEach(model.components, (component) => {
      doTranslation(component, 'translationImpressions', 'impressions');
      doTranslation(component, 'translationRendered', 'rendered');
      doTranslation(component, 'translationRenderedRate', 'renderedRate');
      doTranslation(component, 'translationEarnings', 'earnings');
      doTranslation(component, 'translationRevenueShare', 'revenueShare');
    });

    if (model.reportType === 'SELF_SERVICE') {
      model.parameters.translationRendered = model.parameters.translationRendered || 'Impressions';
      _.forEach(model.components, (component) => {
        component.parameters.translationRendered = model.parameters.translationRendered;
      });
    }
    if (model.reportType !== 'SELF_SERVICE') {
      _.forEach(_.get(model, ['components', 0, 'parameters']), (val, key) => {
        model.parameters[key] = val;
      });
    }
  }
}
